import { Button, Divider, Group, Select, Stack, TextInput } from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useCallback } from 'react'
import { z } from 'zod'

interface Props {
  marinaId?: string
  onCancel: () => void
}

function useContactData(marinaId: number) {
  return useQuery({
    queryKey: ['contact', marinaId],
    queryFn: async (): Promise<Array<ContactFormType>> => {
      return (await fetch(`/api/marina/${marinaId}/`)).json()
    },
  })
}

export function ContactForm(props: Props) {
  const marinaId = parseInt(props.marinaId || '', 10)
  const queryClient = useQueryClient()
  const { data } = useContactData(marinaId)
  const mutation = useMutation({
    mutationFn: updateContact,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['contact'] })
      notifications.show({
        title: 'Marina Updated',
        message: 'Contact information has been updated.',
      })
      props.onCancel()
    },
  })
  const onSubmit = useCallback(
    (data: ContactFormType) => mutation.mutate({ marinaId, data }),
    [marinaId]
  )
  return (
    <>
      {data && (
        <Form
          initial={data as any}
          pending={mutation.isPending}
          onSubmit={onSubmit}
          onCancel={props.onCancel}
        />
      )}
    </>
  )
}

function Form(props: {
  initial: ContactFormType
  onSubmit: (values: ContactFormType) => void
  onCancel: () => void
  pending: boolean
}) {
  const form = useForm<ContactFormType>({
    initialValues: props.initial,
    validate: zodResolver(ContactFormSchema),
  })

  return (
    <form onSubmit={form.onSubmit(props.onSubmit)}>
      <Stack>
        <TextInput
          label="Phone Number"
          type="phone"
          name="phone"
          {...form.getInputProps('contact_phone')}
        />
        <TextInput
          label="E-mail"
          type="email"
          name="email"
          {...form.getInputProps('contact_email')}
        />
        <TextInput
          label="Website URL"
          type="url"
          name="url"
          {...form.getInputProps('contact_url')}
        />
        <Select
          label="Preferred Contact Method"
          data={CONTACT_METHODS}
          {...form.getInputProps('contact_preference')}
        />
        <Divider />
        <Group justify="flex-end" mt="md">
          <Button variant="outline" size="md" onClick={props.onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="md"
            loading={props.pending}
            disabled={props.pending}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </form>
  )
}

const CONTACT_METHODS = [
  { value: 'phone', label: 'Phone' },
  { value: 'email', label: 'E-mail' },
  { value: 'url', label: 'Website URL' },
]

const ContactFormSchema = z.object({
  contact_email: z.string().email().or(z.literal('')),
  contact_phone: z.string(),
  contact_url: z.string().url().or(z.literal('')),
  contact_preference: z.enum(['phone', 'email', 'url']),
})

type ContactFormType = z.infer<typeof ContactFormSchema>

const updateContact = async ({
  marinaId,
  data,
}: {
  marinaId: number
  data: ContactFormType
}) => {
  const response = await axios.put(`/api/marina/${marinaId}/`, data)
  return response.data
}
